import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { DropDownProjectsLists, ProjectBasicInfoRequest, ProjectBasicInfoResponse, ProjectSliceModel, ProjectModelForList, ProjectLocationRequest, ProjectLocation, ProjectFinanceReportResponse, ProjectFinanceReportRequest, ProcedureListModel, Procedure, ProcedureResponse, ProjectResponsibilities, ResponsibilityHistory, ProjectResponsibilityResponse, GetProjects, CostCenterResponsibilityAssignment, ResponsibilityAssignmentResponse, initCostCenterResponsibilityAssignmentModel, getAllProjectAssignmentResponse, projectAssignmentModel, changeProjectAssignmentResponse, CreateProjectParticipantsRequest, CreateProjectParticipantsResponse, GetProjectParticipantsResponse, CreateProjectParticipantSingle, ProjectParticipant, CreateProjectPlanningRequest, ProjectPlanningResponse } from "./project-interfaces";
import { ErrorResponse, FilteredDropDownModelInit, FilteredDropDownRequest, FilteredDropDownResponse, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { Guid } from "guid-typescript";
import { SetPropValue } from "../../../Finance/FinanceDocuments/state/finance-document-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

export const GetActiveProjects = createAsyncThunk<GetProjects, void, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetActiveProjects',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projects/getactiveexternal', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetProjects;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInactiveProjects = createAsyncThunk<ProjectModelForList[], void, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetInactiveProjects',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projects/getinactiveexternal', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProjectModelForList[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetProjectsCreateData = createAsyncThunk<DropDownProjectsLists, void, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetProjectsCreateData',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/projects/getprojectscreatedata', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DropDownProjectsLists;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetProjectBasicInfo = createAsyncThunk<ProjectBasicInfoResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetProjectBasicInfo',
    async (projectId: Guid, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/projectbasicinfo/get?projectId=' + projectId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProjectBasicInfoResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateProject = createAsyncThunk<ProjectBasicInfoResponse, ProjectBasicInfoRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/CreateProject',
    async (project: ProjectBasicInfoRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projectbasicinfo/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(project)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ProjectBasicInfoResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateProject = createAsyncThunk<ProjectBasicInfoResponse, ProjectBasicInfoRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/UpdateProject',
    async (project: ProjectBasicInfoRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projectbasicinfo/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(project)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ProjectBasicInfoResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateProject = createAsyncThunk<Guid, Guid, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/DeactivateProject',
    async (projectId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projectbasicinfo/deactivate?projectId=' + projectId, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as Guid;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateProject = createAsyncThunk<Guid, Guid, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/ReactivateProject',
    async (projectId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projectbasicinfo/reactivate?projectId=' + projectId, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as Guid;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateProjectResponsibilities = createAsyncThunk<ProjectResponsibilityResponse, ProjectResponsibilities, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/CreateProjectResponsibilities',
    async (project: ProjectResponsibilities, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/financial-procedures/responsibilities/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(project)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ProjectResponsibilityResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateProjectResponsibilities = createAsyncThunk<string, ProjectResponsibilities, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/UpdateProjectResponsibilities',
    async (project: ProjectResponsibilities, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/financial-procedures/responsibilities/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(project)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetProjectPlanning = createAsyncThunk<ProjectPlanningResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetProjectPlanning',
    async (projectId: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/planning-templates/get-by-project?projectId=' + projectId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ProjectPlanningResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateProjectPlanning = createAsyncThunk<MainResponse, CreateProjectPlanningRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/CreateProjectPlanning',
    async (request: CreateProjectPlanningRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateProjectLocation = createAsyncThunk<ProjectLocation, ProjectLocationRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/CreateProjectLocation',
    async (request: ProjectLocationRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projectlocation/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ProjectLocation;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateProjectLocation = createAsyncThunk<ProjectLocation, ProjectLocationRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/UpdateProjectLocation',
    async (request: ProjectLocationRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projectlocation/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ProjectLocation;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const GetProjectLocations = createAsyncThunk<ProjectLocation[], string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetProjectLocations',
    async (projectId: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projectlocation/get?projectId=' + projectId, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ProjectLocation[];
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const GetProjectLocationById = createAsyncThunk<ProjectLocationRequest, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetProjeGetProjectLocationByIdctLocations',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projectlocation/getsingleprojectlocation?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ProjectLocationRequest;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetProjectReportById = createAsyncThunk<ProjectFinanceReportResponse, ProjectFinanceReportRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetProjectReportById',
    async (request: ProjectFinanceReportRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/projectreport/get', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ProjectFinanceReportResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetProceduresByProjectId = createAsyncThunk<ProcedureListModel[], string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetProceduresByProjectId',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/financial-procedures/get-by-projectId?projectId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ProcedureListModel[];
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetProcedureByProcedureId = createAsyncThunk<ProcedureResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetProcedureByProcedureId',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/financial-procedures/get-for-cost-centar?procedureId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ProcedureResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetProcedureResponsibilitiesHistory = createAsyncThunk<{ historyLogs: ResponsibilityHistory[] }, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetProcedureResponsibilitiesHistory',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/financial-procedures/responsibilities/history/' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const createResponse = await response.json() as { historyLogs: ResponsibilityHistory[] };
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//Responsibiliti filter list
export const GetPartnerProjectLeadsForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetPartnerProjectLeadsForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetPartnerDeputyProjectLeadsForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetPartnerDeputyProjectLeadsForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetPartnerSalesPersonsForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetPartnerSalesPersonsForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetPartnerAccountingContactsForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetPartnerAccountingContactsForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetExternalIntermediarisForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetExternalIntermediarisForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//GetResponsibilityAssignment
export const GetResponsibilityAssignment = createAsyncThunk<CostCenterResponsibilityAssignment, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetResponsibilityAssignment',
    async (projectId: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/cost-center-responsibilities/get?projectId=' + projectId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CostCenterResponsibilityAssignment;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateResponsibilityAssignment = createAsyncThunk<ResponsibilityAssignmentResponse, CostCenterResponsibilityAssignment, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/CreateResponsibilityAssignment',
    async (projectPlanning: CostCenterResponsibilityAssignment, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/cost-center-responsibilities/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(projectPlanning)
        });
        if (response.status === 200) {
            dispatch(GetResponsibilityAssignment(projectPlanning.projectId!))
            const createResponse = await response.json() as ResponsibilityAssignmentResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateResponsibilityAssignment = createAsyncThunk<ResponsibilityAssignmentResponse, CostCenterResponsibilityAssignment, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/UpdateResponsibilityAssignment',
    async (projectPlanning: CostCenterResponsibilityAssignment, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/cost-center-responsibilities/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(projectPlanning)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ResponsibilityAssignmentResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateProjectAssignment = createAsyncThunk<changeProjectAssignmentResponse, projectAssignmentModel[], { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/CreateProjectAssignment',
    async (data: projectAssignmentModel[], { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/project-assignment/assign', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as changeProjectAssignmentResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdateProjectAssignment = createAsyncThunk<changeProjectAssignmentResponse, projectAssignmentModel, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/UpdateProjectAssignment',
    async (data: projectAssignmentModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/project-assignment/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as changeProjectAssignmentResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const DeleteProjectAssignment = createAsyncThunk<changeProjectAssignmentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/DeleteProjectAssignment',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/project-assignment/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const createResponse = await response.json() as changeProjectAssignmentResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const GetAllProjectAssignments = createAsyncThunk<getAllProjectAssignmentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetAllProjectAssignments',
    async (projectId: string, { rejectWithValue }) => {
        const response = await fetch(`${baseUri}api/project-assignment/project/${projectId}?current=${false}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as getAllProjectAssignmentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);


//Get FIltered partner list
export const GetPartnersForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetPartnersForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateProjectParticipants = createAsyncThunk<GetProjectParticipantsResponse, CreateProjectParticipantSingle, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/CreateProjectParticipants',
    async (data: CreateProjectParticipantSingle, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/project-participants/create-single', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as GetProjectParticipantsResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateProjectParticipants = createAsyncThunk<GetProjectParticipantsResponse, CreateProjectParticipantSingle, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/UpdateProjectParticipants',
    async (data: CreateProjectParticipantSingle, { rejectWithValue }) => {
        const response = await fetch(`${baseUri}api/project-participants/update-single`, {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as GetProjectParticipantsResponse;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteProjectParticipant = createAsyncThunk<GetProjectParticipantsResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/DeleteProjectParticipant',
    async (participantId: string, { rejectWithValue }) => {
        const response = await fetch(`${baseUri}api/project-participants/delete?projectParticipantId=${participantId}`, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as GetProjectParticipantsResponse;
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetParticipantsByProject = createAsyncThunk<GetProjectParticipantsResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ProjectSlice/GetParticipantsByProject',
    async (projectId: string, { rejectWithValue }) => {
        const response = await fetch(`${baseUri}api/project-participants/get?projectId=${projectId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetProjectParticipantsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

const initialValue: ProjectSliceModel = {
    statusCode: '',
    isLoading: false,
    isModalLoading: false,
    message: '',
    showNotification: false,
    reloadData: false,
    listOfActiveProjects: [],
    listOfInactiveProjects: [],
    projectBasicInfo: null,
    projectPlanning: null,
    responsibilityAssignment: initCostCenterResponsibilityAssignmentModel,
    projectAssignments: [],
    dropDownLists: null,
    projectLocations: [],
    reloadLocations: false,
    projectLocation: null,
    projectInvoicesReport: null,
    procedureTabData: { projectProcedures: [], procedureToShow: null },
    filteredPartnerProjectLeadList: FilteredDropDownModelInit,
    filteredPartnerDeputyProjectLeadList: FilteredDropDownModelInit,
    filteredPartnerSalesPersonList: FilteredDropDownModelInit,
    filteredPartnerAccountingContactList: FilteredDropDownModelInit,
    filteredExternalIntermediaryList: FilteredDropDownModelInit,
    participantsList: null,
    filteredPartnersList: FilteredDropDownModelInit
}

export const ProjectSlice = createSlice({
    name: 'ProjectSlice',
    initialState: initialValue,
    reducers: {
        resetActiveProject: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.showNotification = false;
            state.message = '';
            state.reloadData = false;
            state.projectBasicInfo = null;
            state.projectPlanning = null;
            state.responsibilityAssignment = initCostCenterResponsibilityAssignmentModel;
            state.projectAssignments = [];
            state.projectLocations = [];
            state.reloadLocations = false;
            state.projectLocation = null;
            state.projectInvoicesReport = null;
            state.procedureTabData = { projectProcedures: [], procedureToShow: null };
            state.procedureTabData = { projectProcedures: [], procedureToShow: null };
            state.filteredPartnerProjectLeadList = FilteredDropDownModelInit;
            state.filteredPartnerDeputyProjectLeadList = FilteredDropDownModelInit;
            state.filteredPartnerSalesPersonList = FilteredDropDownModelInit;
            state.filteredPartnerAccountingContactList = FilteredDropDownModelInit;
            state.filteredExternalIntermediaryList = FilteredDropDownModelInit;
            state.participantsList = null;
        },
        resetProjectSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.showNotification = false;
            state.message = '';
            state.reloadData = false;
            state.listOfActiveProjects = [];
            state.listOfInactiveProjects = [];
            state.projectBasicInfo = null;
            state.projectPlanning = null;
            state.responsibilityAssignment = initCostCenterResponsibilityAssignmentModel;
            state.projectAssignments = [];
            state.projectLocations = [];
            state.reloadLocations = false;
            state.projectLocation = null;
            state.projectInvoicesReport = null;
            state.procedureTabData = { projectProcedures: [], procedureToShow: null };
            state.filteredPartnerProjectLeadList = FilteredDropDownModelInit;
            state.filteredPartnerDeputyProjectLeadList = FilteredDropDownModelInit;
            state.filteredPartnerSalesPersonList = FilteredDropDownModelInit;
            state.filteredPartnerAccountingContactList = FilteredDropDownModelInit;
            state.filteredExternalIntermediaryList = FilteredDropDownModelInit;
            state.participantsList = null;
        },
        resetProject: (state) => {
            state.projectBasicInfo = null;
            state.projectPlanning = null;
            state.responsibilityAssignment = initCostCenterResponsibilityAssignmentModel;
            state.projectAssignments = [];
            state.isModalLoading = false;
            state.projectLocations = [];
            state.reloadLocations = false;
            state.projectLocation = null;
            state.projectInvoicesReport = null;
            state.procedureTabData = { projectProcedures: [], procedureToShow: null };
            state.procedureTabData = { projectProcedures: [], procedureToShow: null };
            state.filteredPartnerProjectLeadList = FilteredDropDownModelInit;
            state.filteredPartnerDeputyProjectLeadList = FilteredDropDownModelInit;
            state.filteredPartnerSalesPersonList = FilteredDropDownModelInit;
            state.filteredPartnerAccountingContactList = FilteredDropDownModelInit;
            state.filteredExternalIntermediaryList = FilteredDropDownModelInit;
            state.participantsList = null;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
        resetReloadLocations: (state) => {
            state.reloadLocations = false;
        },
        resetProjectLocation: (state) => {
            state.projectLocation = null;
        },
        resetProjectPlanning: (state) => {
            state.projectPlanning = null;
        },
        setHasProjectPlanningState: (state, action: PayloadAction<boolean>) => {
            if (state.projectBasicInfo) {
                state.projectBasicInfo.hasPlanning = action.payload;
            }
        },
        setResponsibilityTabProp: (state, action: PayloadAction<SetPropValue>) => {
            if (state.procedureTabData.procedureToShow && state.procedureTabData.procedureToShow.financialProcedureResponsibilites === null) {
                state.procedureTabData.procedureToShow.financialProcedureResponsibilites =
                {
                    id: null,
                    procedureId: state.procedureTabData.procedureToShow?.financialProcedure.id,
                    projectLeadId: "",
                    deputyProjectLeadId: null,
                    salesPersonId: null,
                    accountingContactId: null,
                    internalIntermediaryId: null,
                    partnerProjectLeadId: null,
                    partnerDeputyProjectLeadId: null,
                    partnerSalesPersonId: null,
                    partnerAccountingContactId: null,
                    externalIntermediaryId: null
                };
            }
            switch (action.payload.propName) {
                case 'projectLeadId':
                    state.procedureTabData.procedureToShow!.financialProcedureResponsibilites.projectLeadId = action.payload.value;
                    break;
                case 'deputyProjectLeadId':
                    state.procedureTabData.procedureToShow!.financialProcedureResponsibilites.deputyProjectLeadId = action.payload.value;
                    break;
                case 'salesPersonId':
                    state.procedureTabData.procedureToShow!.financialProcedureResponsibilites.salesPersonId = action.payload.value;
                    break;
                case 'accountingContactId':
                    state.procedureTabData.procedureToShow!.financialProcedureResponsibilites.accountingContactId = action.payload.value;
                    break;
                case 'internalIntermediaryId':
                    state.procedureTabData.procedureToShow!.financialProcedureResponsibilites.internalIntermediaryId = action.payload.value;
                    break;
                case 'partnerProjectLeadId':
                    state.procedureTabData.procedureToShow!.financialProcedureResponsibilites.partnerProjectLeadId = action.payload.value;
                    break;
                case 'partnerDeputyProjectLeadId':
                    state.procedureTabData.procedureToShow!.financialProcedureResponsibilites.partnerDeputyProjectLeadId = action.payload.value;
                    break;
                case 'partnerSalesPersonId':
                    state.procedureTabData.procedureToShow!.financialProcedureResponsibilites.partnerSalesPersonId = action.payload.value;
                    break;
                case 'partnerAccountingContactId':
                    state.procedureTabData.procedureToShow!.financialProcedureResponsibilites.partnerAccountingContactId = action.payload.value;
                    break;
                case 'externalIntermediaryId':
                    state.procedureTabData.procedureToShow!.financialProcedureResponsibilites.externalIntermediaryId = action.payload.value;
                    break;

                default:
                    console.log("Missing prop name: " + action.payload.propName)
                    break;
            }
        },
        setResponsibilityAssignmentTabProp: (state, action: PayloadAction<SetPropValue>) => {
            switch (action.payload.propName) {
                case 'projectId':
                    state.responsibilityAssignment.projectId = action.payload.value;
                    break;
                case 'technicalLeadId':
                    state.responsibilityAssignment.technicalLeadId = action.payload.value;
                    break;
                case 'deputyTechnicalLeadId':
                    state.responsibilityAssignment.deputyTechnicalLeadId = action.payload.value;
                    break;
                case 'resourceAllocationPermittedPersonIds':
                    state.responsibilityAssignment.resourceAllocationPermittedPersonIds = action.payload.value;
                    break;

                default:
                    console.log("Missing prop name: " + action.payload.propName)
                    break;
            }
        },
    },
    extraReducers(builder) {
        //Get Active External Projects
        builder
            .addCase(GetActiveProjects.fulfilled, (state, action: PayloadAction<GetProjects>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfActiveProjects = action.payload.projects;
                if (state.dropDownLists) {
                    state.dropDownLists.projectNumber = action.payload.projectNumber;
                }
            })
            .addCase(GetActiveProjects.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActiveProjects.pending, state => {
                state.isLoading = true;
            })
        //Get Inactive External Projects
        builder
            .addCase(GetInactiveProjects.fulfilled, (state, action: PayloadAction<ProjectModelForList[]>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfInactiveProjects = action.payload;
            })
            .addCase(GetInactiveProjects.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInactiveProjects.pending, state => {
                state.isLoading = true;
            })
        //Get Projects Create Data
        builder
            .addCase(GetProjectsCreateData.fulfilled, (state, action: PayloadAction<DropDownProjectsLists>) => {
                state.isModalLoading = false;
                state.message = '';
                state.dropDownLists = action.payload;
            })
            .addCase(GetProjectsCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetProjectsCreateData.pending, state => {
                state.isModalLoading = true;
            })
        //Get Project Basic Info
        builder
            .addCase(GetProjectBasicInfo.fulfilled, (state, action: PayloadAction<ProjectBasicInfoResponse>) => {
                state.projectBasicInfo = action.payload
                state.message = '';
                state.isModalLoading = false;
                state.showNotification = true;
            })
            .addCase(GetProjectBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetProjectBasicInfo.pending, state => {
                state.isModalLoading = true;
            })

        //Get Project Basic Info
        builder
            .addCase(GetParticipantsByProject.fulfilled, (state, action: PayloadAction<GetProjectParticipantsResponse>) => {
                state.participantsList = action.payload
                state.message = '';
                state.isModalLoading = false;
                state.showNotification = true;
            })
            .addCase(GetParticipantsByProject.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetParticipantsByProject.pending, state => {
                state.isModalLoading = true;
            })
        //Create External Project
        builder
            .addCase(CreateProject.fulfilled, (state, action: PayloadAction<ProjectBasicInfoResponse>) => {
                state.projectBasicInfo = action.payload;
                state.message = 'The project is successfully created.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
            })
            .addCase(CreateProject.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateProject.pending, state => {
                state.isModalLoading = true;
            })
        //Update External Project
        builder
            .addCase(UpdateProject.fulfilled, (state, action: PayloadAction<ProjectBasicInfoResponse>) => {
                state.projectBasicInfo = action.payload;
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The project basic info is successfully updated.';
                state.showNotification = true;
            })
            .addCase(UpdateProject.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateProject.pending, state => {
                state.isModalLoading = true;
            })
        //Deactivate Project
        builder
            .addCase(DeactivateProject.fulfilled, (state, action: PayloadAction<Guid>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The project basic info is successfully deactivated.';
                state.showNotification = true;
                state.reloadData = true;
            })
            .addCase(DeactivateProject.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateProject.pending, state => {
                state.isModalLoading = true;
            })
        //Reactivate Project
        builder
            .addCase(ReactivateProject.fulfilled, (state, action: PayloadAction<Guid>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The project basic info is successfully reactivated.';
                state.showNotification = true;
                state.reloadData = true;
            })
            .addCase(ReactivateProject.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(ReactivateProject.pending, state => {
                state.isModalLoading = true;
            })
        //Create Project Responsibilties
        builder.addCase(CreateProjectResponsibilities.fulfilled, (state, action) => {
            state.message = 'The project responsibilities are successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
            if (state.procedureTabData.procedureToShow) {
                state.procedureTabData.procedureToShow.financialProcedureResponsibilites = {
                    ...action.meta.arg,
                    id: action.payload.id,
                };
            }
        })
            .addCase(CreateProjectResponsibilities.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateProjectResponsibilities.pending, state => {
                state.isModalLoading = true;
            })
        //Update External Project
        builder.addCase(UpdateProjectResponsibilities.fulfilled, (state, action: PayloadAction<string>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The project responsibilities are successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateProjectResponsibilities.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateProjectResponsibilities.pending, state => {
                state.isModalLoading = true;
            })
        //Get Project Planning
        builder
            .addCase(GetProjectPlanning.fulfilled, (state, action: PayloadAction<ProjectPlanningResponse>) => {
                state.projectPlanning = action.payload;
                state.message = '';
                state.isModalLoading = false;
            })
            .addCase(GetProjectPlanning.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetProjectPlanning.pending, state => {
                state.isModalLoading = true;
            })

        //Create project location
        builder
            .addCase(CreateProjectLocation.fulfilled, (state, action: PayloadAction<ProjectLocation>) => {
                state.isModalLoading = false;
                state.message = 'Location created successfully.';
                state.showNotification = true;
                state.reloadLocations = true;
                state.statusCode = '200';
            })
            .addCase(CreateProjectLocation.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateProjectLocation.pending, state => {
                state.isModalLoading = true;
            })

        //Update project location
        builder
            .addCase(UpdateProjectLocation.fulfilled, (state, action: PayloadAction<ProjectLocation>) => {
                state.isModalLoading = false;
                state.message = 'Location updated successfully.';
                state.showNotification = true;
                state.reloadLocations = true;
                state.statusCode = '200';
            })
            .addCase(UpdateProjectLocation.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateProjectLocation.pending, state => {
                state.isModalLoading = true;
            })

        // Get project locations 
        builder
            .addCase(GetProjectLocations.fulfilled, (state, action: PayloadAction<ProjectLocation[]>) => {
                state.isModalLoading = false;
                state.message = '';
                state.projectLocations = action.payload;
            })
            .addCase(GetProjectLocations.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetProjectLocations.pending, state => {
                state.isModalLoading = true;
            })

        // Get project location by ID
        builder
            .addCase(GetProjectLocationById.fulfilled, (state, action: PayloadAction<ProjectLocationRequest>) => {
                state.isModalLoading = false;
                state.message = '';
                state.projectLocation = action.payload;
            })
            .addCase(GetProjectLocationById.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetProjectLocationById.pending, state => {
                state.isModalLoading = true;
            })

        // Get project report by ID
        builder
            .addCase(GetProjectReportById.fulfilled, (state, action: PayloadAction<ProjectFinanceReportResponse>) => {
                state.isModalLoading = false;
                state.message = '';
                state.projectInvoicesReport = action.payload;
            })
            .addCase(GetProjectReportById.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetProjectReportById.pending, state => {
                state.isModalLoading = true;
            })
        // Get project procedures by ID
        builder
            .addCase(GetProceduresByProjectId.fulfilled, (state, action: PayloadAction<ProcedureListModel[]>) => {
                state.isModalLoading = false;
                state.message = '';
                state.procedureTabData.projectProcedures = action.payload;
            })
            .addCase(GetProceduresByProjectId.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetProceduresByProjectId.pending, state => {
                state.isModalLoading = true;
            })
        // Get procedurs by ID
        builder
            .addCase(GetProcedureByProcedureId.fulfilled, (state, action: PayloadAction<ProcedureResponse>) => {
                state.isModalLoading = false;
                state.message = '';
                state.procedureTabData.procedureToShow = action.payload;
            })
            .addCase(GetProcedureByProcedureId.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = false;
            })
            .addCase(GetProcedureByProcedureId.pending, state => {
                state.isModalLoading = true;
            })
        // Get procedurs by ID
        builder
            .addCase(GetProcedureResponsibilitiesHistory.fulfilled, (state, action: PayloadAction<{ historyLogs: ResponsibilityHistory[] }>) => {
                state.isModalLoading = false;
                state.message = '';
                if (state.procedureTabData.procedureToShow) {
                    state.procedureTabData.procedureToShow.responsibilityHistory = action.payload.historyLogs;
                }
            })
            .addCase(GetProcedureResponsibilitiesHistory.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = false;
            })
            .addCase(GetProcedureResponsibilitiesHistory.pending, state => {
                // state.isModalLoading = true;
            })
        //Get filter list
        builder.addCase(GetPartnerProjectLeadsForDropdown.pending, (state) => {
            state.filteredPartnerProjectLeadList.loading = true;
            state.filteredPartnerProjectLeadList.error = null;
        })
            .addCase(GetPartnerProjectLeadsForDropdown.fulfilled, (state, action) => {
                state.filteredPartnerProjectLeadList.loading = false;
                state.filteredPartnerProjectLeadList.data = action.payload.data;
                state.filteredPartnerProjectLeadList.totalItems = action.payload.totalItems;
                state.filteredPartnerProjectLeadList.totalPages = action.payload.totalPages;
                state.filteredPartnerProjectLeadList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnerProjectLeadsForDropdown.rejected, (state, action) => {
                state.filteredPartnerProjectLeadList.loading = false;
                state.filteredPartnerProjectLeadList.error = action.payload?.message || 'Error fetching data';
            });
        builder.addCase(GetPartnerDeputyProjectLeadsForDropdown.pending, (state) => {
            state.filteredPartnerDeputyProjectLeadList.loading = true;
            state.filteredPartnerDeputyProjectLeadList.error = null;
        })
            .addCase(GetPartnerDeputyProjectLeadsForDropdown.fulfilled, (state, action) => {
                state.filteredPartnerDeputyProjectLeadList.loading = false;
                state.filteredPartnerDeputyProjectLeadList.data = action.payload.data;
                state.filteredPartnerDeputyProjectLeadList.totalItems = action.payload.totalItems;
                state.filteredPartnerDeputyProjectLeadList.totalPages = action.payload.totalPages;
                state.filteredPartnerDeputyProjectLeadList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnerDeputyProjectLeadsForDropdown.rejected, (state, action) => {
                state.filteredPartnerDeputyProjectLeadList.loading = false;
                state.filteredPartnerDeputyProjectLeadList.error = action.payload?.message || 'Error fetching data';
            });
        builder.addCase(GetPartnerSalesPersonsForDropdown.pending, (state) => {
            state.filteredPartnerSalesPersonList.loading = true;
            state.filteredPartnerSalesPersonList.error = null;
        })
            .addCase(GetPartnerSalesPersonsForDropdown.fulfilled, (state, action) => {
                state.filteredPartnerSalesPersonList.loading = false;
                state.filteredPartnerSalesPersonList.data = action.payload.data;
                state.filteredPartnerSalesPersonList.totalItems = action.payload.totalItems;
                state.filteredPartnerSalesPersonList.totalPages = action.payload.totalPages;
                state.filteredPartnerSalesPersonList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnerSalesPersonsForDropdown.rejected, (state, action) => {
                state.filteredPartnerSalesPersonList.loading = false;
                state.filteredPartnerSalesPersonList.error = action.payload?.message || 'Error fetching data';
            });
        builder.addCase(GetPartnerAccountingContactsForDropdown.pending, (state) => {
            state.filteredPartnerAccountingContactList.loading = true;
            state.filteredPartnerAccountingContactList.error = null;
        })
            .addCase(GetPartnerAccountingContactsForDropdown.fulfilled, (state, action) => {
                state.filteredPartnerAccountingContactList.loading = false;
                state.filteredPartnerAccountingContactList.data = action.payload.data;
                state.filteredPartnerAccountingContactList.totalItems = action.payload.totalItems;
                state.filteredPartnerAccountingContactList.totalPages = action.payload.totalPages;
                state.filteredPartnerAccountingContactList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnerAccountingContactsForDropdown.rejected, (state, action) => {
                state.filteredPartnerAccountingContactList.loading = false;
                state.filteredPartnerAccountingContactList.error = action.payload?.message || 'Error fetching data';
            });
        builder.addCase(GetExternalIntermediarisForDropdown.pending, (state) => {
            state.filteredExternalIntermediaryList.loading = true;
            state.filteredExternalIntermediaryList.error = null;
        })
            .addCase(GetExternalIntermediarisForDropdown.fulfilled, (state, action) => {
                state.filteredExternalIntermediaryList.loading = false;
                state.filteredExternalIntermediaryList.data = action.payload.data;
                state.filteredExternalIntermediaryList.totalItems = action.payload.totalItems;
                state.filteredExternalIntermediaryList.totalPages = action.payload.totalPages;
                state.filteredExternalIntermediaryList.currentPage = action.payload.currentPage;
            })
            .addCase(GetExternalIntermediarisForDropdown.rejected, (state, action) => {
                state.filteredExternalIntermediaryList.loading = false;
                state.filteredExternalIntermediaryList.error = action.payload?.message || 'Error fetching data';
            });
        //Create responsibility assignment
        builder
            .addCase(CreateResponsibilityAssignment.fulfilled, (state, action: PayloadAction<ResponsibilityAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = action.payload.message;
                state.showNotification = true;
                state.statusCode = '200';
            })
            .addCase(CreateResponsibilityAssignment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateResponsibilityAssignment.pending, state => {
                state.isModalLoading = true;
            })

        //Update Responsibility Assignment
        builder
            .addCase(UpdateResponsibilityAssignment.fulfilled, (state, action: PayloadAction<ResponsibilityAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = action.payload.message;
                state.showNotification = true;
                state.statusCode = '200';
            })
            .addCase(UpdateResponsibilityAssignment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateResponsibilityAssignment.pending, state => {
                state.isModalLoading = true;
            })

        // Get ResponsibilityAssignment
        builder
            .addCase(GetResponsibilityAssignment.fulfilled, (state, action: PayloadAction<CostCenterResponsibilityAssignment>) => {
                state.isModalLoading = false;
                state.message = '';
                state.responsibilityAssignment = action.payload;
            })
            .addCase(GetResponsibilityAssignment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetResponsibilityAssignment.pending, state => {
                state.isModalLoading = true;
            })
        // Get ResponsibilityAssignment
        builder
            .addCase(GetAllProjectAssignments.fulfilled, (state, action: PayloadAction<getAllProjectAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = '';
                state.projectAssignments = action.payload.projectAssignments;
            })
            .addCase(GetAllProjectAssignments.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetAllProjectAssignments.pending, state => {
                state.isModalLoading = true;
            })
        //Create project assignment
        builder
            .addCase(CreateProjectAssignment.fulfilled, (state, action: PayloadAction<changeProjectAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = action.payload.message;
                state.showNotification = true;
                state.statusCode = '200';
            })
            .addCase(CreateProjectAssignment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateProjectAssignment.pending, state => {
                state.isModalLoading = true;
            })

        //Update Project Assignment
        builder
            .addCase(UpdateProjectAssignment.fulfilled, (state, action: PayloadAction<changeProjectAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = action.payload.message;
                state.showNotification = true;
                state.statusCode = '200';
            })
            .addCase(UpdateProjectAssignment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateProjectAssignment.pending, state => {
                state.isModalLoading = true;
            })
        //Delete Project Assignment
        builder
            .addCase(DeleteProjectAssignment.fulfilled, (state, action: PayloadAction<changeProjectAssignmentResponse>) => {
                state.isModalLoading = false;
                state.message = action.payload.message;
                state.showNotification = true;
                state.statusCode = '200';
            })
            .addCase(DeleteProjectAssignment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteProjectAssignment.pending, state => {
                state.isModalLoading = true;
            })
            //Create Project Participants
            //  .addCase(CreateProjectParticipants.fulfilled, (state, action: PayloadAction<CreateProjectParticipantsResponse>) => {
            //      state.isModalLoading = false;
            //      state.message = action.payload.message;
            //      state.showNotification = true;
            //      state.statusCode = '200';
            //  })
            //  .addCase(CreateProjectParticipants.rejected, (state, action) => {
            //      state.isModalLoading = false;
            //      state.message = action.payload?.message ?? 'Error in application';
            //      state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            //      state.showNotification = true;
            //  })
            //  .addCase(CreateProjectParticipants.pending, state => {
            //      state.isModalLoading = true;
            //  })
            // Update Project Participants
            //  .addCase(UpdateProjectParticipants.fulfilled, (state, action: PayloadAction<CreateProjectParticipantSingle>) => {
            //      state.isModalLoading = false;
            //      // state.message = action.payload.message;
            //      state.showNotification = true;
            //      state.statusCode = '200';
            //  })
            //  .addCase(UpdateProjectParticipants.rejected, (state, action) => {
            //      state.isModalLoading = false;
            //      state.message = action.payload?.message ?? 'Error in application';
            //      state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            //      state.showNotification = true;
            //  })
            //  .addCase(UpdateProjectParticipants.pending, state => {
            //      state.isModalLoading = true;
            //  })
            .addCase(GetPartnersForDropdown.pending, (state) => {
                state.filteredPartnersList.loading = true;
                state.filteredPartnersList.error = null;
            })
            .addCase(GetPartnersForDropdown.fulfilled, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.data = action.payload.data;
                state.filteredPartnersList.totalItems = action.payload.totalItems;
                state.filteredPartnersList.totalPages = action.payload.totalPages;
                state.filteredPartnersList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnersForDropdown.rejected, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.error = action.payload?.message || 'Error fetching data';
            });
    }
});

export const { resetProject, resetProjectSlice, resetStatusCode, resetActiveProject, setHasProjectPlanningState,
    resetProjectLocation, resetProjectPlanning, resetReloadLocations, setResponsibilityTabProp, setResponsibilityAssignmentTabProp } = ProjectSlice.actions;
export default ProjectSlice.reducer;